import { useEffect, useState } from "react";
import api, { formatDateShortIT, STATUS_LABELS } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { openBinary } from "@/lib/pdfHelpers";
import { FileDown, Plus, Send, Truck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Shipments() {
  const { hasRole } = useAuth();
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [readyItems, setReadyItems] = useState([]);
  const [form, setForm] = useState({ manufacturer_id: "", courier: "GLS", tracking_number: "", manufacturer_rma: "", packages: 1, weight_kg: 0, item_ids: [] });
  const [shipDlg, setShipDlg] = useState(null);

  const load = async () => { const r = await api.get("/shipments"); setList(r.data); };
  useEffect(() => { load(); api.get("/manufacturers?active_only=true").then((r) => setManufacturers(r.data)); }, []);

  const openNew = async () => {
    // Load items ready or received
    const [r1, r2] = await Promise.all([
      api.get("/repairs/items?status=Ricevuto_da_AMA&limit=200"),
      api.get("/repairs/items?status=Pronto_per_la_spedizione&limit=200"),
    ]);
    setReadyItems([...r1.data, ...r2.data]);
    setDlg(true);
  };

  const save = async () => {
    if (!form.manufacturer_id) return toast.error("Selezionare produttore");
    if (form.item_ids.length === 0) return toast.error("Selezionare almeno un prodotto");
    try {
      await api.post("/shipments", form);
      toast.success("Spedizione creata");
      setDlg(false); setForm({ manufacturer_id: "", courier: "GLS", tracking_number: "", manufacturer_rma: "", packages: 1, weight_kg: 0, item_ids: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const markShipped = async (s) => {
    setShipDlg(s);
  };
  const confirmShip = async () => {
    if (!shipDlg) return;
    try { await api.post(`/shipments/${shipDlg.id}/ship`, { courier: shipDlg.courier, tracking_number: shipDlg.tracking_number }); toast.success("Spedizione confermata"); setShipDlg(null); load(); }
    catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="shipments-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0a0a2e]">Spedizioni al produttore</h1>
          <p className="text-slate-500">Gestione partite verso i centri riparazione.</p>
        </div>
        {hasRole("warehouse_tech","customer_service") && (
          <Button data-testid="new-shipment-btn" onClick={openNew} className="ama-btn-primary rounded-full"><Plus className="w-4 h-4 mr-1" /> Nuova spedizione</Button>
        )}
      </div>

      <div className="grid gap-3">
        {list.map((s) => (
          <Card key={s.id} className="ama-card border-0 shadow-none" data-testid={`shipment-${s.shipment_number}`}>
            <CardContent className="pt-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-[#d63384]" />
                    <div className="font-heading font-bold text-[#0a0a2e]">{s.shipment_number}</div>
                    <Badge variant={s.status === "spedita" ? "default" : "outline"}>{s.status === "spedita" ? "Spedita" : "In preparazione"}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{s.manufacturer_name} · {s.items?.length} prodotti · Corriere {s.courier || "-"} · Tracking {s.tracking_number || "-"} · {formatDateShortIT(s.shipping_date || s.created_at)}</div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`shipment-pdf-${s.shipment_number}`} variant="outline" size="sm" onClick={() => openBinary(`/shipments/${s.id}/packing-list.pdf`)}><FileDown className="w-4 h-4 mr-1" /> Distinta</Button>
                  {s.status !== "spedita" && hasRole("warehouse_tech") && (
                    <Button data-testid={`shipment-ship-${s.shipment_number}`} className="ama-btn-primary rounded-full" size="sm" onClick={() => markShipped(s)}><Send className="w-4 h-4 mr-1" /> Conferma spedizione</Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {list.length === 0 && <div className="ama-card p-8 text-center text-slate-500">Nessuna spedizione ancora creata.</div>}
      </div>

      {/* New shipment */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nuova spedizione al produttore</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Produttore *</Label>
                <Select value={form.manufacturer_id} onValueChange={(v) => setForm({ ...form, manufacturer_id: v })}>
                  <SelectTrigger data-testid="ship-manufacturer"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{manufacturers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Corriere</Label><Input value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })} /></div>
              <div><Label>RMA produttore</Label><Input value={form.manufacturer_rma} onChange={(e) => setForm({ ...form, manufacturer_rma: e.target.value })} /></div>
              <div><Label>Tracking</Label><Input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} /></div>
              <div><Label>Colli</Label><Input type="number" value={form.packages} onChange={(e) => setForm({ ...form, packages: +e.target.value })} /></div>
              <div><Label>Peso (kg)</Label><Input type="number" step="0.1" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: +e.target.value })} /></div>
            </div>
            <div>
              <Label className="mb-2 block">Prodotti da includere</Label>
              <div className="max-h-64 overflow-auto border rounded-lg divide-y">
                {readyItems.length === 0 && <div className="p-3 text-sm text-slate-500 italic">Nessun prodotto ricevuto o pronto.</div>}
                {readyItems.filter((it) => !form.manufacturer_id || it.manufacturer_id === form.manufacturer_id).map((it) => {
                  const on = form.item_ids.includes(it.id);
                  return (
                    <label key={it.id} className={`flex items-center gap-3 p-2 text-sm cursor-pointer ${on ? "bg-fuchsia-50" : ""}`}>
                      <Checkbox checked={on} onCheckedChange={() => setForm((f) => ({ ...f, item_ids: on ? f.item_ids.filter((x) => x !== it.id) : [...f.item_ids, it.id] }))} />
                      <div className="flex-1">
                        <div className="font-medium">{it.item_number} — {it.manufacturer_name} {it.model}</div>
                        <div className="text-xs text-slate-500">{it.customer_name} · SN {it.serial_number}</div>
                      </div>
                      <div className="text-xs">{STATUS_LABELS[it.current_status]}</div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDlg(false)}>Annulla</Button><Button data-testid="ship-save" className="ama-btn-primary" onClick={save}>Crea spedizione</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm ship dialog */}
      <Dialog open={!!shipDlg} onOpenChange={(v) => !v && setShipDlg(null)}>
        <DialogContent><DialogHeader><DialogTitle>Conferma spedizione</DialogTitle></DialogHeader>
          {shipDlg && (
            <div className="space-y-3">
              <div><Label>Corriere</Label><Input value={shipDlg.courier || ""} onChange={(e) => setShipDlg({ ...shipDlg, courier: e.target.value })} /></div>
              <div><Label>Tracking</Label><Input value={shipDlg.tracking_number || ""} onChange={(e) => setShipDlg({ ...shipDlg, tracking_number: e.target.value })} /></div>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShipDlg(null)}>Annulla</Button><Button className="ama-btn-primary" onClick={confirmShip}>Conferma</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
